import {PayloadAction, createSlice} from '@reduxjs/toolkit';
import {getAccessManagementToolsFailure, getAccessManagementToolsRequest, getAccessManagementToolsSuccess} from 'src/openapi/admin/apis/ToolApi';

import type {Error} from 'src/openapi/admin/models/Error';
import type {Tool} from 'src/openapi/admin/models/Tool';
import type {User} from 'src/openapi/admin/models/User';

type SortDirection = 'ASC' | 'DESC';

type SearchFilters = {
    email?: string,
    toolId?: string,
};

type SortState = {
    sortField?: string,
    sortDirection: SortDirection,
};

type PaginationState = {
    page: number,
    pageSize: number,
};

type InitialState = {
    users: User[],
    totalCount: number,
    isLoading: boolean,
    serverError: Error | null,
    filters: SearchFilters,
    sort: SortState,
    pagination: PaginationState,
    tools: Tool[],
    toolsLoading: boolean,
    selectedPbIds: number[],
    userProfile: User | null,
    profileLoading: boolean,
    emailCheck: Record<string, unknown> | null,
    emailCheckLoading: boolean,
};

export const accessManagementSlice = createSlice({
    name: 'accessManagement',
    initialState: {
        users: [],
        totalCount: 0,
        isLoading: false,
        serverError: null,
        filters: {},
        sort: {sortDirection: 'ASC'},
        pagination: {page: 0, pageSize: 25},
        tools: [],
        toolsLoading: false,
        selectedPbIds: [],
        userProfile: null,
        profileLoading: false,
        emailCheck: null,
        emailCheckLoading: false,
    } as InitialState,
    reducers: {
        setUsers: (state, action: PayloadAction<{items: User[], totalCount: number}>) => {
            state.users = action.payload.items;
            state.totalCount = action.payload.totalCount;
            state.isLoading = false;
            state.serverError = null;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setServerError: (state, action: PayloadAction<Error | null>) => {
            state.serverError = action.payload;
            state.isLoading = false;
        },
        setEmailFilter: (state, action: PayloadAction<string | undefined>) => {
            state.filters.email = action.payload;
            state.pagination.page = 0;
        },
        setToolFilter: (state, action: PayloadAction<string | undefined>) => {
            state.filters.toolId = action.payload;
            state.pagination.page = 0;
        },
        setPage: (state, action: PayloadAction<number>) => {
            state.pagination.page = action.payload;
        },
        setPageSize: (state, action: PayloadAction<number>) => {
            state.pagination.pageSize = action.payload;
            state.pagination.page = 0;
        },
        setSortField: (state, action: PayloadAction<string | undefined>) => {
            state.sort.sortField = action.payload;
        },
        setSortDirection: (state, action: PayloadAction<SortDirection>) => {
            state.sort.sortDirection = action.payload;
        },
        setTools: (state, action: PayloadAction<Tool[]>) => {
            state.tools = action.payload;
            state.toolsLoading = false;
        },
        setToolsLoading: (state, action: PayloadAction<boolean>) => {
            state.toolsLoading = action.payload;
        },
        toggleSelectedPbId: (state, action: PayloadAction<number>) => {
            const idx = state.selectedPbIds.indexOf(action.payload);
            if (idx >= 0) {
                state.selectedPbIds.splice(idx, 1);
            } else {
                state.selectedPbIds.push(action.payload);
            }
        },
        clearSelectedPbIds: state => {
            state.selectedPbIds = [];
        },
        setUser: (state, action: PayloadAction<User | null>) => {
            state.userProfile = action.payload;
            state.profileLoading = false;
        },
        setProfileLoading: (state, action: PayloadAction<boolean>) => {
            state.profileLoading = action.payload;
        },
        setEmailCheck: (state, action: PayloadAction<Record<string, unknown> | null>) => {
            state.emailCheck = action.payload;
            state.emailCheckLoading = false;
        },
        setEmailCheckLoading: (state, action: PayloadAction<boolean>) => {
            state.emailCheckLoading = action.payload;
        },
        reset: state => {
            state.users = [];
            state.totalCount = 0;
            state.isLoading = false;
            state.serverError = null;
            state.filters = {};
            state.sort = {sortDirection: 'ASC'};
            state.pagination = {page: 0, pageSize: 25};
            state.selectedPbIds = [];
            state.emailCheck = null;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(getAccessManagementToolsRequest, state => {
                state.toolsLoading = true;
            })
            .addCase(getAccessManagementToolsSuccess, (state, action) => {
                state.tools = action.payload.data ?? [];
                state.toolsLoading = false;
            })
            .addCase(getAccessManagementToolsFailure, state => {
                state.tools = [];
                state.toolsLoading = false;
            });
    },
});
